import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ProUpgradeCelebration from "@/components/ProUpgradeCelebration";
import AccountActionBanner from "@/components/AccountActionBanner";
import ProTrialBanner from "@/components/ProTrialBanner";

const nav = [
  { to: "/app", label: "Home", end: true, icon: "🏠" },
  { to: "/app/recipes", label: "Recipes", icon: "🍲" },
  { to: "/app/discover", label: "What to eat", icon: "✨" },
  { to: "/app/scan", label: "Scan", icon: "📷" },
  { to: "/app/pantry", label: "Scan fridge", icon: "📸" },
  { to: "/app/food-scanner", label: "Food Scanner", icon: "🔬" },
  { to: "/app/library", label: "Library", icon: "📚" },
  { to: "/app/shopping", label: "Shopping", icon: "🛒" },
  { to: "/app/planner", label: "Planner", icon: "📅" },
  { to: "/app/planner?builder=1", label: "Budget plan", icon: "🎯" },
  { to: "/app/calories", label: "Calories", icon: "🎯" },
  { to: "/app/weight", label: "Weight", icon: "⚖️" },
  { to: "/app/friends", label: "Friends", icon: "💚" },
  { to: "/app/billing", label: "Pro", icon: "🌱" },
  { to: "/app/profile", label: "Profile", icon: "🌿" },
];

export default function AppLayout() {
  const { user, logout, isImpersonating, stopImpersonating } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleStopImpersonating = async () => {
    await stopImpersonating();
    navigate("/owner/users");
  };

  // Close the drawer whenever the route changes (defer setter to next tick so
  // the lint rule doesn't flag a synchronous state-set inside an effect).
  useEffect(() => {
    const t = setTimeout(() => setDrawerOpen(false), 0);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  const activeLabel = nav.find((n) => {
    if (n.end) return location.pathname === n.to;
    return location.pathname.startsWith(n.to);
  })?.label;

  return (
    <div className={`min-h-screen bg-[#FAF9F6] text-[#2A332C] flex ${isImpersonating ? "pt-9" : ""}`}>
      {isImpersonating && (
        <div
          className="fixed top-0 inset-x-0 z-[100] bg-[#3B5A3E] text-white px-4 py-2 flex items-center justify-between gap-3 text-sm shadow-md"
          data-testid="impersonation-banner"
        >
          <div className="flex items-center gap-2 min-w-0">
            <span aria-hidden>👁️</span>
            <span className="truncate">
              Viewing as <strong>{user?.email || "customer"}</strong>
            </span>
          </div>
          <button
            onClick={handleStopImpersonating}
            data-testid="stop-impersonating-btn"
            className="text-xs font-medium bg-white/15 hover:bg-white/25 transition px-3 py-1 rounded-full whitespace-nowrap"
          >
            ← Return to admin
          </button>
        </div>
      )}

      {/* Desktop sidebar (unchanged) */}
      <aside className="hidden md:flex w-64 flex-col border-r border-[#E5E2DA] p-6 bg-[#FAF9F6] sticky top-0 h-screen overflow-y-auto">
        <div className="flex items-center gap-2 text-xl font-medium mb-10 flex-none" style={{ fontFamily: "Outfit, sans-serif" }}>
          <img
            src="/logo.png"
            alt="Shop, Cal & Meal"
            className="w-10 h-10 rounded-full object-cover"
            data-testid="brand-logo-sidebar"
          />
          Shop, Cal & Meal
        </div>
        <nav className="flex flex-col gap-1 flex-none">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-3 rounded-2xl text-sm font-medium flex items-center gap-3 transition ${
                  isActive ? "bg-[#F3F0EA] text-[#3B5A3E]" : "text-[#5C6B61] hover:bg-[#F3F0EA]/60"
                }`
              }
            >
              <span aria-hidden>{n.icon}</span>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto pt-6 border-t border-[#E5E2DA]">
          <div className="flex items-center gap-3" data-testid="sidebar-user">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[#3B5A3E] text-white flex items-center justify-center text-sm font-medium">
                {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name || user?.email}</div>
              <div className="text-xs text-[#8B9690] truncate">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full mt-4 text-xs text-[#5C6B61] hover:text-[#3B5A3E] transition"
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar — hamburger + current page label + sign out */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-[#FAF9F6]/95 backdrop-blur border-b border-[#E5E2DA] px-3 py-3 flex items-center gap-3">
        <button
          onClick={() => setDrawerOpen(true)}
          data-testid="mobile-menu-btn"
          aria-label="Open menu"
          className="w-10 h-10 rounded-full bg-[#F3F0EA] flex items-center justify-center text-lg active:scale-95 transition"
        >
          ☰
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-[#8B9690] leading-tight">Shop, Cal & Meal</div>
          <div className="text-sm font-medium truncate" style={{ fontFamily: "Outfit, sans-serif" }}>
            {activeLabel || "Shop, Cal & Meal"}
          </div>
        </div>
        <button onClick={handleLogout} className="text-xs text-[#5C6B61] px-2" data-testid="mobile-signout">Sign out</button>
      </div>

      {/* Main content area */}
      <main className="flex-1 min-w-0 pt-16 md:pt-0">
        <div className="max-w-6xl mx-auto p-6 md:p-10">
          <AccountActionBanner />
          <ProTrialBanner />
          <Outlet />
        </div>
      </main>

      <ProUpgradeCelebration />

      {/* Mobile slide-out drawer */}
      {drawerOpen && (
        <MobileDrawer
          user={user}
          onClose={() => setDrawerOpen(false)}
          onLogout={handleLogout}
        />
      )}
    </div>
  );
}

function MobileDrawer({ user, onClose, onLogout }) {
  return createPortal(
    <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#2A332C]/45 backdrop-blur-sm ff-fade-quick"
        onClick={onClose}
      />
      {/* Panel */}
      <aside
        className="absolute top-0 left-0 h-full w-72 max-w-[80vw] bg-[#FAF9F6] border-r border-[#E5E2DA] flex flex-col p-5 ff-slide-in-left shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6 flex-none">
          <div className="flex items-center gap-2 font-medium text-lg" style={{ fontFamily: "Outfit, sans-serif" }}>
            <img
              src="/logo.png"
              alt="Shop, Cal & Meal"
              className="w-8 h-8 rounded-full object-cover"
              data-testid="brand-logo-drawer"
            />
            Shop, Cal & Meal
          </div>
          <button
            onClick={onClose}
            data-testid="mobile-drawer-close"
            aria-label="Close menu"
            className="w-9 h-9 rounded-full hover:bg-[#F3F0EA] flex items-center justify-center text-lg"
          >
            ✕
          </button>
        </div>
        <nav className="flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto -mx-2 px-2" data-testid="mobile-drawer-nav">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-3.5 rounded-2xl text-base font-medium flex items-center gap-3 transition ${
                  isActive ? "bg-[#F3F0EA] text-[#3B5A3E]" : "text-[#2A332C] hover:bg-[#F3F0EA]/60"
                }`
              }
            >
              <span aria-hidden className="text-xl w-7 text-center">{n.icon}</span>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="pt-4 border-t border-[#E5E2DA] flex-none">
          <div className="flex items-center gap-3" data-testid="mobile-drawer-user">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[#3B5A3E] text-white flex items-center justify-center text-sm font-medium">
                {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name || user?.email}</div>
              <div className="text-xs text-[#8B9690] truncate">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="w-full mt-3 text-xs text-[#5C6B61] py-2 rounded-full hover:bg-[#F3F0EA]"
          >
            Sign out
          </button>
        </div>
      </aside>
    </div>,
    document.body,
  );
}
